// Chat, feed + likes: the data side.
// On localhost chat + likes run on FAKE data kept in memory (localhost uses the live database, and a message there
// would be real); the feed only reads, so it shows the real posts. localhost/?socialLive uses the real tables. A production build always does.
import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from './supabaseClient';

export const CHAT_MAX = 300;

export const isSocialDemo = () => process.env.NODE_ENV === 'development' && typeof window !== 'undefined'
  && !new URLSearchParams(window.location.search).has('socialLive');

const DEMO_PEOPLE = { 'demo-a': 'Demo Mike', 'demo-b': 'Demo Sam' };
export const demoName = (userId) => DEMO_PEOPLE[userId] || null;

let demoId = 1000;
const demoMessage = (user_id, body, minsAgo = 0, extra = {}) => ({ id: demoId++, user_id, body, created_at: new Date(Date.now() - minsAgo * 60000).toISOString(), ...extra });
const TYPING_SHOWN_MS = 4000;   // someone stops "writing" this long after their last key
const TYPING_SEND_MS = 2000;    // tell the others at most this often
const DEMO_REPLIES = ['No way 😂', 'Bold pick', 'Check the leaderboard 👀', 'See you on Monday 🐐'];

// the leaderboard's fake bets on localhost, so the likes can be tried when nobody has an open bet
export function withDemoBets(bets, index) {
  if (!isSocialDemo() || bets.length > 0 || index > 2) return bets;
  return [{
    id: `demo-bet-${index}`, game_description: 'DEMO · Montréal vs Boston', selection: 'Fake bet to try the likes', bet_type: 'demo',
    stake: 50 * (index + 1), odds: 1.9, potential_win: 95 * (index + 1),
  }];
}

const byTime = (a, b) => new Date(a.created_at) - new Date(b.created_at) || a.id - b.id;

export function useChat(userId) {
  const [messages, setMessages] = useState([]);
  const [failed, setFailed] = useState(false);
  const [online, setOnline] = useState([]);   // who has the site open
  const [typing, setTyping] = useState([]);   // the others writing right now
  const timers = useRef([]);
  const typingTimers = useRef({});
  const channelRef = useRef(null);
  const lastTyped = useRef(0);

  const stopTyping = useCallback((id) => {
    clearTimeout(typingTimers.current[id]);
    setTyping((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : prev));
  }, []);
  const startTyping = useCallback((id) => {
    clearTimeout(typingTimers.current[id]);
    typingTimers.current[id] = setTimeout(() => stopTyping(id), TYPING_SHOWN_MS);
    setTyping((prev) => (prev.includes(id) ? prev : [...prev, id]));
  }, [stopTyping]);

  const add = useCallback((m) => {
    stopTyping(m.user_id);
    setMessages((prev) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m].sort(byTime)));
  }, [stopTyping]);

  useEffect(() => {
    if (!userId) return undefined;
    if (isSocialDemo()) {
      const first = demoMessage('demo-a', 'Who likes the Habs tonight?', 42);
      setMessages([
        first,
        demoMessage('demo-a', 'Taking them on the moneyline', 41),
        demoMessage('demo-b', 'Not me, I saw your last bet 💀', 39, { reply_to: first.id }),
        demoMessage('demo-a', '', 37, { bet: { id: 'demo-share', bet_type: 'winner', game_description: 'Montréal vs Boston', selection: 'Montréal to win', stake: 100, odds: 2.15, potential_win: 215, status: 'pending' } }),
        demoMessage(userId, 'Bold 😂', 30),
        demoMessage(userId, 'See you on Monday', 29),
      ]);
      setOnline([userId, 'demo-a', 'demo-b']);
      const pending = timers.current;
      return () => pending.forEach(clearTimeout);
    }
    let alive = true;
    supabase.from('chat_messages').select('id, user_id, body, created_at, reply_to, bet').order('created_at', { ascending: false }).limit(100)
      .then(({ data, error }) => {
        if (!alive) return;
        if (error) { setFailed(true); return; }
        setMessages((prev) => [...(data || []), ...prev.filter((p) => !(data || []).some((d) => d.id === p.id))].sort(byTime));
      });
    const channel = supabase.channel('site-chat', { config: { presence: { key: userId } } })
      .on('broadcast', { event: 'typing' }, ({ payload }) => { if (payload?.user_id && payload.user_id !== userId) startTyping(payload.user_id); })
      .on('presence', { event: 'sync' }, () => setOnline(Object.keys(channel.presenceState())))
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'chat_messages' }, (p) => add(p.new))
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'chat_messages' }, (p) => setMessages((prev) => prev.filter((m) => m.id !== p.old.id)))
      .subscribe((status) => { if (status === 'SUBSCRIBED') channel.track({ at: Date.now() }); });
    channelRef.current = channel;
    return () => { alive = false; channelRef.current = null; supabase.removeChannel(channel); };
  }, [userId, add, startTyping]);

  // a shared bet can go out with no text. The database fills in the card from the real bet (never the parlay's picks)
  const send = useCallback(async (text, { replyTo = null, bet = null } = {}) => {
    const body = text.trim().slice(0, CHAT_MAX);
    if (!body && !bet) return false;
    if (isSocialDemo()) {
      add(demoMessage(userId, body, 0, { reply_to: replyTo, bet }));
      timers.current.push(setTimeout(() => startTyping('demo-b'), 500));
      timers.current.push(setTimeout(() => add(demoMessage('demo-b', DEMO_REPLIES[demoId % DEMO_REPLIES.length])), 2500));
      return true;
    }
    const { data, error } = await supabase.from('chat_messages').insert({ user_id: userId, body, reply_to: replyTo, bet_id: bet?.id ?? null }).select('id, user_id, body, created_at, reply_to, bet').single();
    if (error) return false;
    add(data);
    return true;
  }, [userId, add, startTyping]);

  const notifyTyping = useCallback(() => {
    if (!channelRef.current || Date.now() - lastTyped.current < TYPING_SEND_MS) return;
    lastTyped.current = Date.now();
    channelRef.current.send({ type: 'broadcast', event: 'typing', payload: { user_id: userId } });
  }, [userId]);

  const remove = useCallback(async (id) => {
    if (!isSocialDemo()) {
      const { error } = await supabase.from('chat_messages').delete().eq('id', id);
      if (error) return;
    }
    setMessages((prev) => prev.filter((m) => m.id !== id));
  }, []);

  return { messages, send, remove, failed, online, typing, notifyTyping };
}

// the likes on screen: { key: [{ id, user_id }] }. A key is what's liked: 'bet:123', 'jackpot:2026-09:<user>'…
// A bet has one key, so its likes are the same on the Leaderboard and on both of its Feed posts.
export function useLikes(keys, userId, enabled, scope = 'feed') {
  const [byKey, setByKey] = useState({});
  const joined = enabled ? keys.join(',') : '';
  const seeded = useRef(new Set());

  useEffect(() => {
    if (!joined || !isSocialDemo()) return;
    // a few fake likes from the fake people
    const fresh = {};
    joined.split(',').forEach((k, i) => {
      if (seeded.current.has(k)) return;
      seeded.current.add(k);
      fresh[k] = Object.keys(DEMO_PEOPLE).slice(0, i % 3).map((user_id) => ({ id: `${k}-${user_id}`, user_id }));
    });
    setByKey((prev) => ({ ...prev, ...fresh }));
  }, [joined]);

  useEffect(() => {
    if (!joined || isSocialDemo()) return undefined;
    let alive = true;
    supabase.from('feed_likes').select('id, item_key, user_id, created_at').in('item_key', joined.split(','))
      .then(({ data, error }) => {
        if (!alive || error) return;
        const next = {};
        (data || []).forEach((r) => { (next[r.item_key] = next[r.item_key] || []).push(r); });
        setByKey(next);
      });
    const channel = supabase.channel(`likes-${scope}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'feed_likes' }, ({ new: r }) => setByKey((prev) => {
        const list = prev[r.item_key] || [];
        return list.some((x) => x.id === r.id || x.user_id === r.user_id) ? prev : { ...prev, [r.item_key]: [...list, r] };
      }))
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'feed_likes' }, ({ old }) => setByKey((prev) => {
        const next = {};
        Object.keys(prev).forEach((k) => { next[k] = prev[k].filter((x) => x.id !== old.id); });
        return next;
      }))
      .subscribe();
    return () => { alive = false; supabase.removeChannel(channel); };
  }, [joined, scope]);

  const toggle = useCallback(async (key) => {
    const mine = (byKey[key] || []).find((r) => r.user_id === userId);
    const set = (fn) => setByKey((prev) => ({ ...prev, [key]: fn(prev[key] || []) }));
    if (mine) {
      set((l) => l.filter((r) => r.user_id !== userId));
      if (isSocialDemo()) return;
      const { error } = await supabase.from('feed_likes').delete().eq('id', mine.id);
      if (error) set((l) => [...l, mine]);
      return;
    }
    // shows right away; the saved row replaces it (or it's taken back if the save failed)
    const temp = { id: `temp-${key}`, user_id: userId };
    set((l) => [...l, temp]);
    if (isSocialDemo()) return;
    const { data, error } = await supabase.from('feed_likes').insert({ item_key: key, user_id: userId }).select('id, item_key, user_id, created_at').single();
    set((l) => {
      const rest = l.filter((r) => r.id !== temp.id && !(data && r.id === data.id));
      return error ? rest : [...rest, data];
    });
  }, [byKey, userId]);

  return { byKey, toggle };
}

const FEED_MAX = 60;
export const FEED_BETS = 100;   // one post can hold many bets, so the bets get more room than the other kinds
const SPIN_MIN = 100;   // smaller Spin wins would drown the feed
export const parlayLegs = (bet) => {
  try { return JSON.parse(bet.bet_value).legs.length; } catch { return bet.legs || 0; }
};

async function loadFeed(userId, boost) {
  const BET = 'id, user_id, game_id, game_description, selection, odds, stake, potential_win, payout, bet_type, bet_value, status, result, created_at, settled_at';
  const [placed, won, board, parlayWins, predict, jackpots, spins] = await Promise.all([
    supabase.from('bets').select(BET).neq('status', 'cancelled').order('created_at', { ascending: false }).limit(FEED_BETS),
    supabase.from('bets').select(BET).eq('status', 'settled').eq('result', 'won').order('settled_at', { ascending: false }).limit(FEED_MAX),
    supabase.rpc('parlay_board'),         // others' combined bets (the picks stay hidden)
    supabase.rpc('parlay_wins_feed'),
    supabase.from('predict_entries').select('user_id, game_date, payout, paid_out_at').gt('payout', 0).order('game_date', { ascending: false }).limit(10),
    supabase.from('jackpots').select('month, winners, drawn_at').not('drawn_at', 'is', null).order('month', { ascending: false }).limit(2),
    supabase.from('slot_spins').select('user_id, spin_date, payout, created_at').gte('payout', SPIN_MIN).order('created_at', { ascending: false }).limit(10),
  ]);
  if (placed.error) throw placed.error;

  const items = [];
  const addBet = (kind, bet, at) => items.push({ kind, likeKey: `bet:${bet.id}`, user_id: bet.user_id, bet, legs: parlayLegs(bet), at });
  (placed.data || []).forEach((b) => addBet('placed', b, b.created_at));
  (board.data || []).filter((b) => b.user_id !== userId).forEach((b) => addBet('placed', { ...b, bet_type: 'parlay' }, b.created_at));
  (won.data || []).forEach((b) => addBet('won', b, b.settled_at || b.created_at));
  (parlayWins.data || []).filter((b) => b.user_id !== userId).forEach((b) => addBet('won', { ...b, bet_type: 'parlay' }, b.settled_at));
  (predict.data || []).forEach((e) => items.push({ kind: 'predict', likeKey: `predict:${e.game_date}:${e.user_id}`, user_id: e.user_id, amount: +e.payout, at: e.paid_out_at || e.game_date }));
  (jackpots.data || []).forEach((j) => (j.winners || []).forEach((w) => items.push({ kind: 'jackpot', likeKey: `jackpot:${j.month}:${w.user_id}`, user_id: w.user_id, name: w.username, amount: +w.amount, place: w.place, month: j.month, at: j.drawn_at })));
  (spins.data || []).forEach((r) => items.push({ kind: 'spin', likeKey: `spin:${r.spin_date}:${r.user_id}`, user_id: r.user_id, amount: +r.payout, at: r.created_at }));
  if (boost?.endsAt) {
    const ends = new Date(boost.endsAt);
    items.push({ kind: 'boost', likeKey: `event:boost:${boost.endsAt.slice(0, 10)}`, at: new Date(ends.getTime() - 3 * 86400000).toISOString() });
  }
  return items.sort((a, b) => new Date(b.at) - new Date(a.at));
}

// the Feed's posts: only loads while its page is open, then follows the bets live
export function useFeed(enabled, userId, boost) {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const boostEnd = boost?.endsAt ? boost.endsAt.slice(0, 16) : '';
  const boostRef = useRef(boost);
  boostRef.current = boost;

  useEffect(() => {
    if (!enabled || !userId) return undefined;
    let alive = true;
    let timer = null;
    const refresh = () => loadFeed(userId, boostRef.current)
      .then((next) => { if (alive) { setItems(next); setFailed(false); setLoaded(true); } })
      .catch(() => { if (alive) { setFailed(true); setLoaded(true); } });
    refresh();
    const soon = () => { clearTimeout(timer); timer = setTimeout(refresh, 1500); };
    const channel = supabase.channel('feed-bets')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'bets' }, soon)
      .subscribe();
    const poll = setInterval(refresh, 60 * 1000);   // the site's wins aren't followed live
    return () => { alive = false; clearTimeout(timer); clearInterval(poll); supabase.removeChannel(channel); };
  }, [enabled, userId, boostEnd]);

  return { items, loaded, failed };
}
